import React, { useState, useEffect } from 'react'

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&family=Source+Serif+4:ital,opsz,wght@0,8..60,300;0,8..60,400;0,8..60,600;1,8..60,300&family=Space+Mono:wght@400;700&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --ink: #0f0e0c;
    --paper: #f5f0e8;
    --paper-dark: #ede7d6;
    --steel: #1a2e3b;
    --steel-mid: #3a5568;
    --accent: #1a5276;
    --accent-light: #2980b9;
    --gold: #c9943a;
    --gold-light: #f0c96e;
    --rule: #d4c9b0;
    --danger: #922b21;
  }

  .article-root {
    background: var(--paper);
    min-height: 100vh;
    font-family: 'Source Serif 4', Georgia, serif;
    color: var(--ink);
    overflow-x: hidden;
  }

  /* HEADER */
  .article-header {
    background: var(--steel);
    position: relative;
    overflow: hidden;
  }

  .header-noise {
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.06'/%3E%3C/svg%3E");
    opacity: 0.4;
    pointer-events: none;
  }

  .header-stripe {
    height: 4px;
    background: linear-gradient(90deg, var(--accent-light) 0%, var(--gold) 50%, var(--accent-light) 100%);
  }

  .header-inner {
    max-width: 900px;
    margin: 0 auto;
    padding: 48px 40px 40px;
    position: relative;
    z-index: 1;
  }

  .journal-label {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--gold-light);
    opacity: 0.8;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .journal-label::before, .journal-label::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--gold);
    opacity: 0.3;
  }

  .article-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(24px, 4vw, 44px);
    font-weight: 900;
    line-height: 1.15;
    color: #fff;
    margin-bottom: 28px;
    letter-spacing: -0.02em;
  }

  .article-title .accent-word {
    color: var(--gold-light);
    font-style: italic;
  }

  .author-block {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 0;
    border-top: 1px solid rgba(201,148,58,0.3);
  }

  .author-avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--accent-light), var(--gold));
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Playfair Display', serif;
    font-size: 18px;
    font-weight: 700;
    color: #fff;
    flex-shrink: 0;
  }

  .author-info { display: flex; flex-direction: column; }

  .author-name {
    font-family: 'Playfair Display', serif;
    font-size: 15px;
    font-weight: 700;
    font-style: italic;
    color: #fff;
  }

  .author-role {
    font-family: 'Space Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--gold-light);
    opacity: 0.7;
    margin-top: 2px;
  }

  /* HERO IMAGE */
  .hero-image-section {
    position: relative;
    width: 100%;
    overflow: hidden;
    background: var(--steel);
  }

  .hero-image-section img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.7;
    filter: sepia(15%) contrast(1.15);
  }

  .hero-caption {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 20px 40px 16px;
    background: linear-gradient(to top, rgba(15,14,12,0.85) 0%, transparent 100%);
  }

  .hero-caption-text {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--gold-light);
    opacity: 0.9;
  }

  /* MAIN BODY */
  .article-body {
    max-width: 900px;
    margin: 0 auto;
    padding: 0 40px 80px;
  }

  /* STAT ROW */
  .stat-row {
    display: flex;
    gap: 16px;
    margin: 44px 0 36px;
    flex-wrap: wrap;
  }

  .stat-card {
    flex: 1;
    min-width: 160px;
    background: var(--steel);
    padding: 22px 20px;
    position: relative;
    overflow: hidden;
  }

  .stat-card::before {
    content: '';
    position: absolute;
    bottom: -16px;
    right: -16px;
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: rgba(201,148,58,0.1);
  }

  .stat-num {
    font-family: 'Playfair Display', serif;
    font-size: 34px;
    font-weight: 900;
    color: var(--gold-light);
    line-height: 1;
    margin-bottom: 6px;
  }

  .stat-desc {
    font-family: 'Source Serif 4', serif;
    font-size: 12px;
    line-height: 1.5;
    color: rgba(255,255,255,0.75);
    font-style: italic;
  }

  /* TABLE */
  .data-table-wrap {
    margin: 24px 0 28px;
    overflow-x: auto;
  }

  .data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
  }

  .data-table thead tr {
    background: var(--steel);
    color: #fff;
  }

  .data-table thead th {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    padding: 12px 16px;
    text-align: left;
    font-weight: 700;
  }

  .data-table tbody tr {
    border-bottom: 1px solid var(--rule);
    transition: background 0.15s;
  }

  .data-table tbody tr:hover {
    background: var(--paper-dark);
  }

  .data-table tbody td {
    padding: 11px 16px;
    color: var(--steel);
    font-weight: 300;
  }

  .data-table tbody td:first-child {
    font-weight: 600;
    color: var(--ink);
  }

  .share-bar {
    display: inline-block;
    height: 6px;
    border-radius: 3px;
    background: linear-gradient(90deg, var(--accent-light), var(--gold));
    margin-left: 10px;
    vertical-align: middle;
    opacity: 0.7;
  }

  /* ALERT BOX */
  .alert-box {
    margin: 28px 0;
    padding: 20px 24px;
    border-left: 4px solid var(--danger);
    background: rgba(146,43,33,0.06);
  }

  .alert-label {
    font-family: 'Space Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--danger);
    font-weight: 700;
    margin-bottom: 8px;
  }

  .alert-text {
    font-size: 14.5px;
    line-height: 1.75;
    color: #2a2a2a;
    font-style: italic;
    font-weight: 300;
  }

  /* POLICY CARDS */
  .policy-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 16px;
    margin: 24px 0;
  }

  .policy-card {
    background: var(--paper-dark);
    border: 1px solid var(--rule);
    padding: 20px;
    position: relative;
  }

  .policy-num {
    font-family: 'Playfair Display', serif;
    font-size: 36px;
    font-weight: 900;
    color: var(--gold);
    opacity: 0.3;
    line-height: 1;
    margin-bottom: 8px;
  }

  .policy-title {
    font-family: 'Playfair Display', serif;
    font-size: 15px;
    font-weight: 700;
    color: var(--steel);
    margin-bottom: 8px;
    line-height: 1.3;
  }

  .policy-body {
    font-size: 13px;
    line-height: 1.65;
    color: #444;
    font-weight: 300;
  }

  /* SITUATION TABLE */
  .situation-table {
    width: 100%;
    border-collapse: collapse;
    margin: 20px 0;
    font-size: 14px;
  }

  .situation-table td {
    padding: 10px 14px;
    border-bottom: 1px solid var(--rule);
    vertical-align: top;
  }

  .situation-table td:first-child {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.1em;
    color: var(--accent-light);
    font-weight: 700;
    white-space: nowrap;
    width: 180px;
    padding-top: 13px;
  }

  .situation-table td:last-child {
    font-weight: 300;
    line-height: 1.7;
    color: #2a2a2a;
  }

  /* SECTION */
  .section {
    margin-bottom: 40px;
    padding-bottom: 40px;
    border-bottom: 1px solid var(--rule);
    animation: fadeUp 0.5s ease both;
  }

  .section:last-child { border-bottom: none; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .section-number {
    font-family: 'Space Mono', monospace;
    font-size: 11px;
    letter-spacing: 0.15em;
    color: var(--gold);
    font-weight: 700;
    margin-bottom: 6px;
  }

  .section-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(18px, 2.5vw, 26px);
    font-weight: 700;
    color: var(--steel);
    margin-bottom: 18px;
    line-height: 1.25;
    border-bottom: 2px solid var(--rule);
    padding-bottom: 10px;
  }

  .subsection-title {
    font-family: 'Playfair Display', serif;
    font-size: 17px;
    font-weight: 700;
    color: var(--steel);
    margin: 24px 0 12px;
    padding-left: 12px;
    border-left: 3px solid var(--gold);
  }

  .section-body {
    font-size: 16px;
    line-height: 1.85;
    color: #2a2a2a;
    font-weight: 300;
  }

  .section-body p { margin-bottom: 14px; }

  .section-body strong {
    color: var(--steel);
    font-weight: 600;
  }

  /* BULLET LIST */
  .bullet-list {
    list-style: none;
    margin: 14px 0 14px 8px;
    padding: 0;
  }

  .bullet-list li {
    padding: 6px 0 6px 24px;
    position: relative;
    font-size: 15.5px;
    line-height: 1.7;
    color: #2a2a2a;
    font-weight: 300;
  }

  .bullet-list li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 15px;
    width: 10px;
    height: 2px;
    background: var(--accent-light);
  }

  /* PULL QUOTE */
  .pull-quote {
    margin: 36px -12px;
    padding: 28px 32px;
    border-top: 3px solid var(--gold);
    border-bottom: 3px solid var(--gold);
    text-align: center;
  }

  .pull-quote-text {
    font-family: 'Playfair Display', serif;
    font-size: clamp(16px, 2vw, 21px);
    font-style: italic;
    line-height: 1.6;
    color: var(--steel);
  }

  /* TOC */
  .toc-sidebar {
    background: var(--paper-dark);
    border: 1px solid var(--rule);
    padding: 24px 28px;
    margin: 44px 0;
  }

  .toc-title {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--steel-mid);
    margin-bottom: 14px;
    font-weight: 700;
  }

  .toc-list {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .toc-item {
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 13px;
    color: var(--steel);
    cursor: pointer;
    padding: 3px 0;
    transition: color 0.2s;
  }

  .toc-item:hover { color: var(--accent-light); }

  .toc-num {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    color: var(--gold);
    flex-shrink: 0;
  }

  /* FOOTER */
  .article-footer {
    background: var(--steel);
    padding: 32px 40px;
    text-align: center;
  }

  .footer-logo {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--gold-light);
    opacity: 0.6;
  }

  @media (max-width: 600px) {
    .header-inner, .article-body, .hero-caption, .article-footer { padding-left: 20px; padding-right: 20px; }
    .stat-row { flex-direction: column; }
    .hero-image-section { height: 220px; }
    .policy-grid { grid-template-columns: 1fr; }
    .situation-table td:first-child { width: 120px; }
  }
`

const policies = [
  { num: '1', title: 'Expand Strategic Petroleum Reserves', body: 'Increase SPR capacity to cover a larger share of India’s import requirements for greater supply security.' },
  { num: '2', title: 'Diversify Energy Supply Sources', body: 'Reduce dependence on vulnerable transit corridors by diversifying imports from Russia, Africa, Latin America and other regions.' },
  { num: '3', title: 'Strengthen LNG Infrastructure', body: 'Accelerate development of LNG terminals, pipeline networks, and domestic gas distribution to support industrial transitions.' },
  { num: '4', title: 'Accelerate Renewable Energy Deployment', body: 'Fast-track solar, wind, and green hydrogen projects to reduce long-term dependence on imported fossil fuels.' },
  { num: '5', title: 'Promote Cleaner Fuels for Steel Sector', body: 'Facilitate reliable and affordable access to natural gas and other cleaner fuels for energy-intensive steel units.' },
  { num: '6', title: 'Support Technology Upgradation in Secondary Steel', body: 'Provide financial and policy incentives for modernization, energy efficiency, and adoption of direct/hot charging technologies.' },
  { num: '7', title: 'Develop Integrated Steel Clusters', body: 'Promote cluster-based integrated steel parks with shared infrastructure to enable efficient production systems.' },
  { num: '8', title: 'Enhance International Energy Cooperation', body: 'Strengthen multilateral partnerships and diplomatic engagement for stable energy supply chains.' },
]

const tocItems = [
  { num: '1', label: 'Introduction' },
  { num: '2', label: 'India’s Climate Commitments and Global Responsibilities' },
  { num: '3', label: 'Energy Security Challenges Amid Global Geopolitical Tensions' },
  { num: '4', label: 'Macroeconomic Implications of Rising Energy Prices' },
  { num: '5', label: 'Implications for the Steel Industry' },
  { num: '6', label: 'Structural Transformation within the Steel Industry' },
  { num: '7', label: 'India’s Strategic Energy Response' },
  { num: '8', label: 'Strategic Way Forward' },
]

const Article3 = () => {
  const scrollTo = (idx) => {
    const el = document.getElementById(`section-${idx}`)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="article-root">
      <style>{styles}</style>

      {/* HEADER */}
      <header className="article-header">
        <div className="header-noise" />
        <div className="header-stripe" />
        <div className="header-inner">
          <div className="journal-label">AIIFA Policy Report · April 2026 · Energy Security Series</div>
          <h1 className="article-title">
            Strengthening Energy Security and Supporting the <span className="accent-word">Green Transition</span> of India’s Steel Sector
          </h1>
          <div className="author-block">
            <div className="author-avatar">KA</div>
            <div className="author-info">
              <span className="author-name">Kamal Aggarwal</span>
              <span className="author-role">Hon. Secretary General, AIIFA</span>
            </div>
          </div>
        </div>
      </header>

      {/* HERO IMAGE - You can change the image URL if needed */}
      <div className="hero-image-section">
        <img
          src="https://res.cloudinary.com/dxvbzgdgw/image/upload/q_auto/f_auto/v1775557036/0416a4a9-f9ca-4151-9985-872608d3f654_yu2m8g.jpg"
          alt="Steel industry and energy transition"
        />
        <div className="hero-caption">
          <span className="hero-caption-text">Energy security and green steel transition amid global uncertainties</span>
        </div>
      </div>

      {/* BODY */}
      <main className="article-body">

        {/* STAT ROW - Updated with relevant stats from document */}
        <div className="stat-row">
          <div className="stat-card">
            <div className="stat-num">85%</div>
            <div className="stat-desc">of India’s crude oil is imported</div>
          </div>
          <div className="stat-card">
            <div className="stat-num">55%</div>
            <div className="stat-desc">of natural gas consumption is imported</div>
          </div>
          <div className="stat-card">
            <div className="stat-num">20%</div>
            <div className="stat-desc">of global oil trade passes through Strait of Hormuz</div>
          </div>
        </div>

        {/* TOC */}
        <div className="toc-sidebar">
          <div className="toc-title">Contents</div>
          <ul className="toc-list">
            {tocItems.map((item, i) => (
              <li key={i} className="toc-item" onClick={() => scrollTo(i)}>
                <span className="toc-num">{item.num}.</span>
                <span>{item.label}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* PULL QUOTE */}
        <div className="pull-quote">
          <p className="pull-quote-text">
            "Energy security has emerged as a critical policy priority for India while advancing its climate commitments and ensuring industrial competitiveness."
          </p>
        </div>

        {/* SECTION 1 — Introduction */}
        <section id="section-0" className="section" style={{ animationDelay: '0s' }}>
          <div className="section-number">Section 01</div>
          <h2 className="section-title">Introduction</h2>
          <div className="section-body">
            <p>Energy security has emerged as a critical policy priority for India in the context of increasing geopolitical tensions and disruptions in global energy markets. Recent developments in West Asia, coupled with vulnerabilities around strategic maritime chokepoints such as the Strait of Hormuz—through which a substantial portion of the world’s crude oil and liquefied natural gas (LNG) supplies transit—have heightened volatility in global energy prices and supply chains.</p>
            <p>For an energy-import-dependent economy like India, such disruptions highlight the urgent need to strengthen energy resilience while simultaneously advancing climate commitments, ensuring industrial competitiveness, and maintaining macroeconomic stability.</p>
          </div>
        </section>

        {/* SECTION 2 — Climate Commitments */}
        <section id="section-1" className="section" style={{ animationDelay: '0.05s' }}>
          <div className="section-number">Section 02</div>
          <h2 className="section-title">India’s Climate Commitments and Global Responsibilities</h2>
          <div className="section-body">
            <p>The Paris Agreement, adopted under the United Nations Framework Convention on Climate Change (UNFCCC) in 2015, represents a landmark global framework to address climate change. India has consistently emphasized the principle of Common but Differentiated Responsibilities and Respective Capabilities (CBDR-RC).</p>
            <p>Reinforcing its commitment to global climate action, India announced its Net Zero emissions target by 2070 during the COP26 summit. As part of its climate roadmap, India has pledged to:</p>
            <ul className="bullet-list">
              <li>Achieve 500 GW of non-fossil fuel energy capacity by 2030</li>
              <li>Meet 50% of its energy requirements from renewable sources</li>
              <li>Reduce one billion tonnes of projected carbon emissions</li>
              <li>Lower the carbon intensity of GDP by 45% from 2005 levels</li>
            </ul>
          </div>
        </section>

        {/* SECTION 3 — Energy Security Challenges */}
        <section id="section-2" className="section" style={{ animationDelay: '0.1s' }}>
          <div className="section-number">Section 03</div>
          <h2 className="section-title">Energy Security Challenges Amid Global Geopolitical Tensions</h2>
          <div className="section-body">
            <p>The ongoing geopolitical tensions in West Asia have introduced significant uncertainties into global energy markets. Nearly 20% of global oil trade passes through the Strait of Hormuz.</p>
            <p>India currently imports approximately 85% of its crude oil requirements and around 55% of its natural gas consumption, making the economy particularly sensitive to fluctuations in global energy prices.</p>
          </div>
        </section>

        {/* SECTION 4 — Macroeconomic Implications */}
        <section id="section-3" className="section" style={{ animationDelay: '0.15s' }}>
          <div className="section-number">Section 04</div>
          <h2 className="section-title">Macroeconomic Implications of Rising Energy Prices</h2>
          <div className="section-body">
            <p>Credit rating agencies and economic analysts have highlighted the potential macroeconomic risks associated with sustained high energy prices.</p>
            <p>If crude prices remain above $85 per barrel, India’s real GDP growth may moderate. In more adverse scenarios of $120 per barrel, GDP growth could slow further and inflation could rise significantly, exceeding the RBI’s upper tolerance band.</p>
          </div>
        </section>

        {/* SECTION 5 — Implications for Steel Industry */}
        <section id="section-4" className="section" style={{ animationDelay: '0.2s' }}>
          <div className="section-number">Section 05</div>
          <h2 className="section-title">Implications for the Steel Industry</h2>
          <div className="section-body">
            <p>The steel sector is among the most energy-intensive industries and plays a vital role in India’s industrial growth, infrastructure development, and long-term decarbonization pathway.</p>
            <p>The use of natural gas as a transition fuel has the potential to reduce carbon emissions compared with conventional fossil fuels while improving energy efficiency in steelmaking operations.</p>
            <p>However, recent challenges relating to natural gas availability and pricing have increased operational costs for steel producers and slowed the transition toward cleaner steelmaking technologies.</p>
          </div>
        </section>

        {/* SECTION 6 — Structural Transformation */}
        <section id="section-5" className="section" style={{ animationDelay: '0.25s' }}>
          <div className="section-number">Section 06</div>
          <h2 className="section-title">Structural Transformation within the Steel Industry</h2>
          <div className="section-body">
            <p>The steel industry is undergoing a structural transformation driven by technological innovation and efficiency requirements. The traditional model of standalone rolling mills and independent induction furnace units is gradually shifting toward integrated production systems, particularly direct charging and hot charging technologies.</p>
            <p>These systems offer significant reduction in energy consumption, lower production costs, and reduced carbon emissions. However, transitioning to integrated systems requires substantial capital investment, especially for SMEs in the secondary steel sector.</p>
          </div>
        </section>

        {/* SECTION 7 — India’s Strategic Energy Response */}
        <section id="section-6" className="section" style={{ animationDelay: '0.3s' }}>
          <div className="section-number">Section 07</div>
          <h2 className="section-title">India’s Strategic Energy Response</h2>
          <div className="section-body">
            <p>Despite global uncertainties, India has adopted several proactive measures including diversification of energy imports, expansion of Strategic Petroleum Reserves, rapid growth in renewable energy capacity (approaching 190 GW), and promotion of alternative fuels such as ethanol blending, compressed bio-gas, and green hydrogen.</p>
          </div>
        </section>

        {/* SECTION 8 — Strategic Way Forward */}
        <section id="section-7" className="section" style={{ animationDelay: '0.35s' }}>
          <div className="section-number">Section 08</div>
          <h2 className="section-title">Strategic Way Forward</h2>
          <div className="section-body">
            <p>To ensure long-term energy security while supporting industrial growth and climate commitments, the following policy measures may be considered:</p>
            <div className="policy-grid">
              {policies.map((p, i) => (
                <div className="policy-card" key={i}>
                  <div className="policy-num">{p.num}</div>
                  <div className="policy-title">{p.title}</div>
                  <div className="policy-body">{p.body}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

      </main>

      {/* FOOTER */}
      <footer className="article-footer">
        <div className="footer-logo">AIIFA · All India Induction Furnace Association</div>
      </footer>
    </div>
  )
}

export default Article3